import { logger } from "./logger"

/**
 * Sistema avançado de logging para desenvolvimento baseado em Pino
 *
 * Características:
 * - Suporta diferentes níveis de log (debug, info, warn, error, fatal)
 * - Permite adicionar contextos e metadados
 * - Serializa automaticamente objetos complexos
 * - Controla exibição de logs por ambiente
 * - Suporta encadeamento de métodos
 * - Permite medição de performance com traces
 */
class DevLogger {
  constructor() {
    this.isEnabled = process.env.NODE_ENV !== "production" || process.env.DEBUG === "true"
    this.baseLogger = logger.child({ component: "dev" })
    this.traces = new Map() // Armazena os traces de performance
  }

  /**
   * Cria uma instância de logger com um contexto específico
   * @param {string} context - Nome do contexto (componente, serviço, etc)
   * @param {Object} metadata - Metadados adicionais para todos os logs deste contexto
   * @returns {DevLogger} Nova instância de logger com contexto
   */
  context(context, metadata = {}) {
    const contextLogger = new DevLogger()
    contextLogger.baseLogger = this.baseLogger.child({
      context,
      ...metadata,
    })
    return contextLogger
  }

  /**
   * Log genérico que aceita múltiplos argumentos
   * @param {string} level - Nível de log (debug, info, warn, error, fatal)
   * @param  {...any} args - Mensagem e dados adicionais
   */
  _log(level, ...args) {
    if (!this.isEnabled && level === "debug") return

    if (args.length === 0) return

    if (args.length === 1) {
      this.baseLogger[level](args[0])
      return
    }

    // Separar a mensagem dos dados
    const message = args[0]
    const data = args.slice(1)

    if (data.length === 1) {
      this.baseLogger[level]({ data: data[0] }, message)
    } else {
      this.baseLogger[level]({ data }, message)
    }
  }

  /**
   * Inicia um trace para medir performance
   * @param {string} label - Identificador único do trace
   * @returns {DevLogger} - Permite encadeamento de métodos
   */
  traceStart(label) {
    if (!this.isEnabled) return this

    this.traces.set(label, {
      start: performance.now(),
      label,
    })
    this.baseLogger.debug(
      {
        trace: "start",
        label,
      },
      `Iniciando trace: ${label}`,
    )

    return this
  }

  /**
   * Finaliza um trace e registra o tempo decorrido
   * @param {string} label - Identificador do trace iniciado anteriormente
   * @returns {DevLogger} - Permite encadeamento de métodos
   */
  traceEnd(label) {
    if (!this.isEnabled) return this

    const trace = this.traces.get(label)
    if (!trace) {
      this.baseLogger.warn(
        {
          trace: "missing",
          label,
        },
        `Trace não encontrado: ${label}`,
      )
      return this
    }

    const duration = performance.now() - trace.start
    this.traces.delete(label)

    this.baseLogger.debug(
      {
        trace: "end",
        label,
        durationMs: duration.toFixed(2),
      },
      `Trace ${label}: ${duration.toFixed(2)}ms`,
    )

    return this
  }

  /**
   * Cria um grupo de logs relacionados
   * @param {string} groupLabel - Nome do grupo
   * @returns {Object} Objeto com métodos de logging para o grupo
   */
  group(groupLabel) {
    const groupLogger = this.baseLogger.child({ group: groupLabel })
    const groupMethods = {}

    // Cria versões dos métodos de log para o grupo
    ;["debug", "info", "warn", "error", "fatal"].forEach((level) => {
      groupMethods[level] = (...args) => {
        if (!this.isEnabled && level === "debug") return groupMethods

        if (args.length === 1) {
          groupLogger[level](args[0])
        } else {
          const message = args[0]
          const data = args.slice(1)
          groupLogger[level]({ data: data.length === 1 ? data[0] : data }, message)
        }

        return groupMethods
      }
    })

    // Método para finalizar o grupo e retornar o logger original
    groupMethods.end = () => this

    return groupMethods
  }

  // Métodos de log por níveis
  debug(...args) {
    this._log("debug", ...args)
    return this
  }
  info(...args) {
    this._log("info", ...args)
    return this
  }
  warn(...args) {
    this._log("warn", ...args)
    return this
  }
  error(...args) {
    this._log("error", ...args)
    return this
  }
  fatal(...args) {
    this._log("fatal", ...args)
    return this
  }

  // Método compatível com o uso atual
  log(...args) {
    return this.debug(...args)
  }
}

// Instância padrão para uso rápido
const devLog = (...args) => {
  const devLogger = new DevLogger()
  return devLogger.debug(...args)
}

// Permite acesso a instância para contextos específicos
devLog.create = (context, metadata) => {
  return new DevLogger().context(context, metadata)
}

// Para manter compatibilidade com código existente
export default devLog

// Exportações avançadas para uso mais elaborado
export { DevLogger, devLog }
